import React from 'react'
import { FieldError } from '@/components/ui/field'
import {
  AsyncCombobox,
  type AsyncComboboxPage
} from '@/components/ui/async-combobox'
import { ReferenceInputMethodProps } from '../types'
import { ReferenceCard, ReferenceLoadingCard } from '../ReferenceCard'

interface SearchInputProps<T extends object>
  extends ReferenceInputMethodProps<T> {
  onSearch: (name: string) => Promise<T[]>
  resourceToLabel: (resource: T) => string
  icon?: React.ReactNode
  placeholder?: string | undefined
}

export default function SearchInput<T extends object>({
  onSearch,
  inputId,
  onBlur,
  onAdd,
  required,
  onFocus,
  RenderResource,
  resource,
  onRemove,
  error,
  icon,
  resourceToLabel,
  placeholder,
  isLoading
}: SearchInputProps<T>) {
  async function loadOptions(query: string): Promise<AsyncComboboxPage<T>> {
    const results = await onSearch(query).catch(() => [] as T[])
    return {
      options: results.map((result, index) => ({
        label: resourceToLabel(result),
        value: `${index}-${resourceToLabel(result)}`,
        data: result
      }))
    }
  }

  if (isLoading) {
    return (
      <>
        <ReferenceLoadingCard icon={icon} />
        <FieldError>{error}</FieldError>
      </>
    )
  }

  if (resource) {
    return (
      <>
        <ReferenceCard icon={icon} onRemove={onRemove}>
          <RenderResource resource={resource} />
        </ReferenceCard>
        <FieldError>{error}</FieldError>
      </>
    )
  }

  return (
    <>
      <div className='mb-1'>
        <AsyncCombobox
          id={inputId}
          aria-label={placeholder}
          placeholder={placeholder}
          searchPlaceholder={placeholder}
          required={required}
          // Reference search by name only queries once 3 characters are entered.
          minSearchLength={3}
          debounceMs={500}
          value={null}
          onChange={option => (option ? onAdd(option.data) : onRemove())}
          loadOptions={loadOptions}
          onBlur={onBlur}
          onFocus={onFocus}
        />
      </div>
      <FieldError>{error}</FieldError>
    </>
  )
}
